/** XeAgents.ai**/
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  Bell,
  BarChart3,
  Calendar,
  Check,
  Gift,
  Globe,
  HeartPulse,
  Mic,
  PhoneOff,
  Phone,
  Pause,
  Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useAuthDialog } from "@/components/landing/AuthDialog";
import { usePilotRequestDialog } from "@/components/landing/PilotRequestDialog";
import { AuthStorage } from "@/lib/auth-storage";
import { FREE_VOICE_CREDITS } from "@/lib/free-credits";
import { useFreeCreditTerms } from "@/components/landing/useFreeCreditTerms";
import { scrollToSection } from "@/lib/section-nav";
import { OTHER_SECTORS } from "@/components/landing/SectorsStrip";

/** Demo line answered by the AI virtual receptionist. */
const DEMO_PHONE_DISPLAY = "+61 2 5502 5923";
const DEMO_PHONE_TEL = "+61255025923";

const TRUST_POINTS = [
  { icon: BarChart3, key: "trustTranscript" },
  { icon: Users, key: "trustHandoff" },
  { icon: Globe, key: "trustMultilingual" },
];

/**
 * An illustration of the product, not a readout.
 *
 * Everything in it is invented - there is no tenant behind these numbers - so
 * it is labelled as a preview and marked aria-hidden. A screenshot-shaped panel
 * that quotes figures without saying where they came from is the kind of thing
 * a reader reasonably takes for real data.
 */
function ProductPreview() {
  const { t } = useTranslation("landing");
  return (
    <div
      className="relative rounded-2xl border border-white/10 bg-slate-900/80 p-3 shadow-2xl shadow-violet-950/50 backdrop-blur sm:p-4"
      aria-hidden="true"
    >
      <div className="flex items-center justify-between border-b border-white/10 pb-3">
        <span className="text-sm font-bold text-white">
          V<span className="text-violet-400">o</span>icePro
        </span>
        <span className="flex items-center gap-2 text-xs text-slate-400">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-violet-500/20 text-[10px] font-semibold text-violet-300">
            A
          </span>
          {t("preview.clinic")}
        </span>
      </div>

      <div className="grid gap-3 pt-3 sm:grid-cols-[1fr_1.1fr]">
        {/* Live call panel */}
        <div className="rounded-xl bg-slate-950/60 p-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-white">{t("preview.liveCall")}</span>
            <span className="flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-medium text-emerald-300">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              {t("preview.live")}
            </span>
          </div>

          <div className="mt-4 flex flex-col items-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-violet-400/40 bg-violet-500/10">
              <Phone className="h-6 w-6 text-violet-300" />
            </div>
            <div className="mt-3 flex h-6 items-end gap-0.5">
              {[6, 12, 20, 14, 24, 10, 18, 8, 16, 22, 12, 6].map((height, index) => (
                <span
                  key={index}
                  className="w-1 rounded-full bg-gradient-to-t from-violet-500 to-purple-400"
                  style={{ height }}
                />
              ))}
            </div>
            <p className="mt-3 text-xs font-medium text-white">{t("preview.aiAgent")}</p>
            <p className="text-[10px] text-slate-400">{t("preview.speaking")}</p>
          </div>

          <p className="mt-3 rounded-lg bg-white/5 p-2 text-[10px] leading-relaxed text-slate-300">
            "{t("preview.greeting")}"
          </p>

          <div className="mt-3 flex items-center justify-center gap-4 text-slate-400">
            <span className="flex flex-col items-center gap-1 text-[9px]">
              <Mic className="h-4 w-4" />
              {t("preview.mute")}
            </span>
            <span className="flex flex-col items-center gap-1 text-[9px] text-rose-300">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-rose-500/90">
                <PhoneOff className="h-3.5 w-3.5 text-white" />
              </span>
              {t("preview.endCall")}
            </span>
            <span className="flex flex-col items-center gap-1 text-[9px]">
              <Pause className="h-4 w-4" />
              {t("preview.hold")}
            </span>
          </div>
        </div>

        {/* Transcript panel */}
        <div className="rounded-xl bg-slate-950/60 p-3">
          <div className="flex items-center gap-3 border-b border-white/10 pb-2 text-[11px]">
            <span className="border-b-2 border-violet-400 pb-1 font-medium text-white">{t("preview.transcript")}</span>
            <span className="text-slate-500">{t("preview.details")}</span>
          </div>
          <div className="mt-3 space-y-2">
            {[
              { who: t("preview.aiAgent"), text: t("preview.greeting") },
              { who: t("preview.patient"), text: t("preview.patientLine") },
              { who: t("preview.aiAgent"), text: t("preview.agentLine") },
            ].map((line, index) => (
              <div key={index} className="rounded-lg bg-white/5 p-2">
                <p className="text-[9px] font-semibold text-violet-300">{line.who}</p>
                <p className="mt-0.5 text-[10px] leading-relaxed text-slate-300">{line.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <p className="pt-3 text-center text-[10px] font-medium uppercase tracking-wider text-slate-500">
        {t("preview.caption")}
      </p>
    </div>
  );
}

export function VoiceProHero() {
  const { t } = useTranslation("landing");
  const freeCreditTerms = useFreeCreditTerms();
  const { openAuth } = useAuthDialog();
  const { open: openPilotRequest } = usePilotRequestDialog();
  const shouldReduceMotion = useReducedMotion();
  const isAuthenticated = AuthStorage.isAuthenticated();
  const isSuperAdmin = AuthStorage.isSuperAdmin();

  const handleClaimCredits = () => {
    if (isAuthenticated) {
      window.location.href = isSuperAdmin ? "/admin" : "/app";
      return;
    }
    openAuth("register");
  };

  const fade = (delay: number) =>
    shouldReduceMotion
      ? { initial: { opacity: 1 }, animate: { opacity: 1 } }
      : {
          initial: { opacity: 0, y: 24 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.6, delay, ease: [0.25, 0.4, 0.25, 1] as const },
        };

  return (
    <div
      className="relative overflow-hidden bg-slate-950 pb-12 pt-28 sm:pt-32 md:pb-16 lg:pt-36"
      data-testid="hero-section"
    >
      {/* Ambient backdrop */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_70%_60%_at_20%_0%,rgba(139,92,246,0.30),transparent)]" />
      <div className="pointer-events-none absolute -right-24 top-24 h-96 w-96 rounded-full bg-violet-600/15 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-12">
          {/* ───────── Copy column ───────── */}
          <div>
            <motion.span
              {...fade(0)}
              className="inline-flex items-center gap-2 rounded-full border border-teal-400/30 bg-teal-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-teal-300"
            >
              <HeartPulse className="h-3.5 w-3.5" />
              {t("hero.eyebrow")}
            </motion.span>

            <motion.h1
              {...fade(0.05)}
              className="mt-4 text-3xl font-bold leading-[1.12] tracking-tight text-white sm:text-4xl md:text-5xl lg:text-[3.25rem]"
              data-testid="hero-headline"
            >
              {t("hero.title1")}
              <span className="block bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">
                {t("hero.title2")}
              </span>
            </motion.h1>

            <motion.p
              {...fade(0.1)}
              className="mt-5 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
              data-testid="hero-subheadline"
            >
              {t("hero.subtitle")}
            </motion.p>

            {/* ───────── The offer ───────── */}
            <motion.div
              {...fade(0.15)}
              className="mt-8 rounded-2xl border border-violet-400/30 bg-gradient-to-br from-violet-950/80 to-purple-950/60 p-5 shadow-lg shadow-violet-950/50 sm:p-6"
              data-testid="hero-free-credits"
            >
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-violet-500/20">
                  <Gift className="h-5 w-5 text-violet-300" />
                </span>
                <div className="min-w-0">
                  <p className="text-lg font-bold text-white sm:text-xl">
                    {t("hero.offerBefore")}{" "}
                    <span className="text-violet-300">
                      {t("hero.offerHighlight", { count: FREE_VOICE_CREDITS })}
                    </span>{" "}
                    {t("hero.offerAfter")}
                  </p>
                  <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                    {freeCreditTerms.map((term) => (
                      <li key={term} className="flex items-center gap-1.5 text-xs text-slate-300">
                        <Check className="h-3.5 w-3.5 flex-shrink-0 text-violet-400" />
                        {term}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                <Button
                  size="lg"
                  onClick={handleClaimCredits}
                  className="group h-12 flex-1 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-base font-semibold text-white shadow-lg shadow-violet-900/40 hover:from-violet-500 hover:to-purple-500"
                  data-testid="button-hero-free-credits"
                >
                  {t("credits.get", { count: FREE_VOICE_CREDITS })}
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => openPilotRequest()}
                  className="h-12 flex-1 rounded-xl border-white/20 bg-white/5 text-base font-medium text-white hover:bg-white/10 hover:text-white"
                  data-testid="button-hero-demo"
                >
                  {t("hero.bookDemo")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>

              <button
                type="button"
                onClick={() => scrollToSection("#use-cases")}
                className="mt-3 text-sm font-medium text-violet-300 hover:text-violet-200"
                data-testid="button-hero-use-cases"
              >
                {t("hero.seeUseCases")} &rarr;
              </button>

              <p className="mt-4 flex items-center gap-2 text-xs text-slate-400">
                <Check className="h-3.5 w-3.5 flex-shrink-0 text-emerald-400" />
                {t("hero.includedNote", { count: FREE_VOICE_CREDITS })}
              </p>

              <a
                href={`tel:${DEMO_PHONE_TEL}`}
                className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-teal-300 hover:text-teal-200"
                data-testid="link-hero-demo-call"
              >
                <Phone className="h-4 w-4" />
                {t("hero.callDemo")} <span className="font-semibold">{DEMO_PHONE_DISPLAY}</span>
              </a>
            </motion.div>

            <motion.ul {...fade(0.2)} className="mt-6 flex flex-wrap gap-x-6 gap-y-2">
              {TRUST_POINTS.map(({ icon: Icon, key }) => (
                <li key={key} className="flex items-center gap-2 text-sm text-slate-400">
                  <Icon className="h-4 w-4 flex-shrink-0 text-violet-400" />
                  {t(`hero.${key}`)}
                </li>
              ))}
            </motion.ul>

            {/* Healthcare is the focus; the other sectors are secondary and link to the strip below. */}
            <motion.div
              {...fade(0.22)}
              className="mt-6 flex flex-wrap items-center gap-2"
              data-testid="hero-sector-chips"
            >
              <button
                type="button"
                onClick={() => scrollToSection("#outcomes")}
                className="inline-flex items-center gap-1.5 rounded-full bg-teal-400/15 px-3 py-1.5 text-xs font-semibold text-teal-200 ring-1 ring-teal-400/40 hover:bg-teal-400/25"
              >
                <HeartPulse className="h-3.5 w-3.5" />
                {t("hero.sectorHealthcare")}
              </button>
              <span className="px-1 text-xs text-slate-500">{t("hero.sectorAlso")}</span>
              {OTHER_SECTORS.map(({ icon: Icon, key }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => scrollToSection("#sectors")}
                  className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-slate-300 ring-1 ring-white/15 hover:bg-white/5 hover:text-white"
                >
                  <Icon className="h-3.5 w-3.5" />
                  {t(`sectors.${key}.title`)}
                </button>
              ))}
            </motion.div>
          </div>

          {/* ───────── Product column ───────── */}
          <motion.div {...fade(0.25)} className="relative">
            <div className="absolute -top-6 right-2 z-10 hidden rounded-2xl bg-gradient-to-r from-fuchsia-600 to-violet-600 px-4 py-2.5 text-white shadow-xl shadow-violet-900/40 sm:block">
              <p className="text-sm font-bold leading-tight">
                {t("hero.badgeLine1", { count: FREE_VOICE_CREDITS })}
                <br />
                {t("hero.badgeLine2")}
              </p>
              <p className="text-[10px] text-white/80">{t("hero.badgeSub")}</p>
            </div>

            <ProductPreview />

            {/* Workflow outcomes. Same caveat as the preview: an illustration, so no figures. */}
            <div
              className="mt-4 grid grid-cols-3 gap-2 rounded-2xl border border-white/10 bg-slate-900/70 p-3 backdrop-blur"
              aria-hidden="true"
            >
              {[
                { icon: Calendar, label: t("hero.outcomeBooked") },
                { icon: Bell, label: t("hero.outcomeConfirmed") },
                { icon: Users, label: t("hero.outcomeHanded") },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2">
                  <Icon className="h-4 w-4 flex-shrink-0 text-violet-400" />
                  <p className="text-[11px] font-medium text-slate-200 sm:text-xs">{label}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default VoiceProHero;
